import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  SIGNOFF_ROLES,
  SIGNOFF_ROLE_BLURB,
  SIGNOFF_ROLE_LABEL,
  isSomebody,
  type TheWorld,
} from '../signoff-policy';
import { displayName, initials } from '../people';
import VendorTag from './VendorTag';
import { effectiveRole, ROLE_LABEL } from '../permissions';
import type { SignoffRole, SignoffStageMember, SignoffStageMode } from '../types';
import Icon from './Icon';

/**
 * ── THE SHARED STAGE BUILDER — one editor for every ordered-stage chain ────────
 *
 * Extracted verbatim from the sign-off rule screen so a second chain — the
 * payment-approval chain (executive-approver tier) — reuses the SAME card, the
 * SAME signer typeahead and the SAME answer-rule control rather than a parallel
 * copy that would drift. A stage member (`SignoffStageMember`) and the answer rule
 * (`SignoffStageMode`) are the one shape both chains share; a `PaymentStage` adds
 * only its value band, carried in through `StageCard`'s optional `extra` slot.
 *
 * Nothing here knows which chain it is editing: it reads the minimal
 * `EditableStage` shape and calls back. The owner screen holds the model, the save
 * path and the warnings.
 */

/** The least a stage must be for this card to edit it — satisfied by both
 *  `SignoffStage` and `PaymentStage`, so neither has to change. */
export interface EditableStage {
  id: string;
  label: string;
  members: SignoffStageMember[];
  mode: SignoffStageMode;
  optional?: boolean;
}

/** A person a stage member resolves to today, in the position the ENGINE will
 *  freeze — a person is positioned by the board rank they hold, so the chip and
 *  the preview agree with `resolveStageMembers` rather than a job title. */
export function personPosition(email: string, world: TheWorld): string {
  const role = effectiveRole({
    email,
    admins: world.access.admins,
    defaults: world.roles as Record<string, string>,
    on: world.projectRoles as Record<string, Record<string, string>>,
    projectId: world.projectId,
  });
  return ROLE_LABEL[role];
}

/** Two members are the same signer when they are the same kind and point at the
 *  same thing — so the typeahead never offers, and a stage never keeps, a rank or
 *  a person twice. */
export function sameMember(a: SignoffStageMember, b: SignoffStageMember): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'role') return a.role === b.role;
  if (a.kind === 'person') return (a.personRef ?? '').toLowerCase() === (b.personRef ?? '').toLowerCase();
  return (
    (a.name ?? '').trim().toLowerCase() === (b.name ?? '').trim().toLowerCase() &&
    (a.position ?? '').trim().toLowerCase() === (b.position ?? '').trim().toLowerCase()
  );
}

/** How a signer chip reads, worked out from the member and the world so the chip
 *  says the same thing the sheet will. */
export function chipOf(
  m: SignoffStageMember,
  world: TheWorld,
  profiles: TheWorld['profiles']
): { icon: 'users' | 'mail' | null; avatar?: string; name: string; sub: string; external?: boolean } {
  if (m.kind === 'role' && m.role) {
    /* The role label already says who it means ("Project managers on this
       board"); only the owner earns a sub, the person it resolves to today. */
    const sub = m.role === 'owner' && isSomebody(world.owner) ? `${world.owner.name} — ${world.owner.position}` : '';
    return { icon: 'users', name: SIGNOFF_ROLE_LABEL[m.role], sub };
  }
  if (m.kind === 'person' && m.personRef) {
    const email = m.personRef.toLowerCase();
    return {
      icon: null,
      avatar: initials(email, profiles),
      name: displayName(email, profiles),
      sub: personPosition(email, world),
    };
  }
  return { icon: 'mail', name: (m.name ?? '').trim(), sub: (m.position ?? '').trim(), external: true };
}

export function StageCard({
  stage,
  at,
  total,
  world,
  profiles,
  canSettings,
  dragging,
  onDragStart,
  onDragEnd,
  onDropHere,
  onRename,
  onRemove,
  onMove,
  onAddMember,
  onRemoveMember,
  onMode,
  extra,
  summary,
}: {
  stage: EditableStage;
  at: number;
  total: number;
  world: TheWorld;
  profiles: TheWorld['profiles'];
  canSettings: boolean;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropHere: () => void;
  onRename: (label: string) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  onAddMember: (m: SignoffStageMember) => void;
  onRemoveMember: (i: number) => void;
  onMode: (mode: SignoffStageMode) => void;
  /** An extra per-stage control, rendered after the answer-rule block — the
   *  payment chain passes its value band here; the sign-off chain passes nothing. */
  extra?: ReactNode;
  /** Replaces the default one-line summary when given, so a chain can word its own
   *  ("…applies to every payment"). Omitted → the sign-off summary. */
  summary?: ReactNode;
}) {
  const n = stage.members.length;
  const count = `${n} ${n === 1 ? 'person' : 'people'}`;
  /* The stage's answer rule read into one of three shapes the editor shows.
     A quorum is only meaningful with three or more members (two is already
     "everyone" or "any one"), and is clamped to 2…n-1 — the same bounds
     readStageMode enforces on the way back in. */
  const quorum = typeof stage.mode === 'object' ? stage.mode.quorum : null;
  const sel: 'all' | 'anyOne' | 'quorum' =
    stage.mode === 'anyOne' ? 'anyOne' : quorum != null ? 'quorum' : 'all';
  const qShown = Math.min(Math.max(quorum ?? 2, 2), Math.max(2, n - 1));

  return (
    <div
      className={`sr-stage ${dragging ? 'is-dragging' : ''}`}
      onDragOver={(e) => {
        if (canSettings) e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDropHere();
      }}
    >
      <div className="sr-stage-head">
        <span
          className="sr-grip"
          draggable={canSettings}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          role="button"
          tabIndex={-1}
          aria-hidden="true"
          title="Drag to reorder"
        >
          <Icon name="grip" size={18} />
        </span>
        <span className="sr-stage-no">{at + 1}</span>
        <label className="sr-only" htmlFor={`stage-name-${stage.id}`}>
          Stage {at + 1} name <span className="req">*</span>
        </label>
        <input
          id={`stage-name-${stage.id}`}
          className="input sr-stage-name"
          value={stage.label}
          placeholder="Name this stage"
          disabled={!canSettings}
          onChange={(e) => onRename(e.target.value)}
        />
        {canSettings && (
          <span className="sr-move">
            <button
              className="btn-icon"
              aria-label={`Move ${stage.label || `stage ${at + 1}`} earlier`}
              disabled={at === 0}
              onClick={() => onMove(-1)}
            >
              <Icon name="chevron" size={16} className="sr-move-up" />
            </button>
            <button
              className="btn-icon"
              aria-label={`Move ${stage.label || `stage ${at + 1}`} later`}
              disabled={at === total - 1}
              onClick={() => onMove(1)}
            >
              <Icon name="chevron" size={16} />
            </button>
          </span>
        )}
        {canSettings && (
          <button className="link sr-stage-remove" onClick={onRemove}>
            Remove
          </button>
        )}
      </div>

      <div className="sr-signers">
        {stage.members.map((m, i) => {
          const chip = chipOf(m, world, profiles);
          return (
            <span className={`sr-signer is-${m.kind}`} key={`${m.kind}-${i}`}>
              {chip.avatar ? (
                <span className="sr-signer-av" aria-hidden="true">
                  {chip.avatar}
                </span>
              ) : chip.icon ? (
                <Icon name={chip.icon} size={15} className="sr-signer-ic" />
              ) : null}
              <span className="sr-signer-name">{chip.name || 'Unnamed'}</span>
              {chip.sub && <span className="sr-signer-sub">{chip.sub}</span>}
              {chip.external && <span className="sr-signer-badge">External</span>}
              {canSettings && (
                <button
                  className="sr-signer-x"
                  aria-label={`Remove ${chip.name || 'this signer'} from ${stage.label || 'this stage'}`}
                  onClick={() => onRemoveMember(i)}
                >
                  <Icon name="close" size={13} />
                </button>
              )}
            </span>
          );
        })}
        {canSettings && <AddSigner stage={stage} world={world} profiles={profiles} onAdd={onAddMember} />}
      </div>

      {/* HOW MANY OF THEM must answer — everyone, any one, or at least N. Only
          meaningful with two or more signers; "at least N" needs three or more,
          since with two "everyone" and "any one" already cover it. */}
      {n >= 2 && (
        <div className="sr-mode">
          <span className="sr-toggle-name">How many must sign?</span>
          <div className="seg sr-mode-seg" role="group" aria-label="How many of these signers must answer">
            <button
              type="button"
              className={`seg-btn ${sel === 'all' ? 'active' : ''}`}
              aria-pressed={sel === 'all'}
              disabled={!canSettings}
              onClick={() => onMode('all')}
            >
              Everyone
            </button>
            <button
              type="button"
              className={`seg-btn ${sel === 'anyOne' ? 'active' : ''}`}
              aria-pressed={sel === 'anyOne'}
              disabled={!canSettings}
              onClick={() => onMode('anyOne')}
            >
              Any one
            </button>
            {n >= 3 && (
              <button
                type="button"
                className={`seg-btn ${sel === 'quorum' ? 'active' : ''}`}
                aria-pressed={sel === 'quorum'}
                disabled={!canSettings}
                onClick={() => onMode({ quorum: qShown })}
              >
                At least…
              </button>
            )}
          </div>
          {sel === 'quorum' && n >= 3 && (
            <div className="sr-quorum" role="group" aria-label="How many must sign">
              <button
                type="button"
                className="btn btn-small sr-quorum-step"
                aria-label="One fewer"
                disabled={!canSettings || qShown <= 2}
                onClick={() => onMode({ quorum: Math.max(2, qShown - 1) })}
              >
                −
              </button>
              <span className="sr-quorum-n" aria-live="polite">{qShown}</span>
              <button
                type="button"
                className="btn btn-small sr-quorum-step"
                aria-label="One more"
                disabled={!canSettings || qShown >= n - 1}
                onClick={() => onMode({ quorum: Math.min(n - 1, qShown + 1) })}
              >
                +
              </button>
              <span className="sr-toggle-why">of {count} must sign.</span>
            </div>
          )}
        </div>
      )}

      {/* An extra per-stage control (the payment chain's value band). */}
      {extra}

      {/* OPTIONAL STAGES are held back from R1 — see the sign-off rule screen. */}

      {/* THE ONE-LINE SUMMARY, in plain words, matching the control above. A chain
          may word its own; otherwise the sign-off wording. */}
      {summary !== undefined ? (
        summary
      ) : (
        <p className="sr-note sr-stage-sum">
          {n === 0
            ? 'No signers yet.'
            : sel === 'anyOne'
              ? `Any one of ${count} signs${stage.optional ? ', and this stage is optional.' : '.'}`
              : sel === 'quorum'
                ? `At least ${qShown} of ${count} sign${stage.optional ? ', and this stage is optional.' : '.'}`
                : stage.optional
                  ? 'Everyone here signs, but this stage is optional — the sheet clears without it.'
                  : `Everyone here signs. ${count}.`}
        </p>
      )}
    </div>
  );
}

/**
 * ── ADD A SIGNER — the typeahead — configurable sign-offs R1 ──────────────────
 *
 * A rank, a person on the board, or somebody outside it by email. It closes on a
 * click away and on Escape, the app standard. An outside person is finished on a
 * small form — name and position — the same two fields a signed sheet has a
 * column for, so they can be sent their own signing link.
 */
export function AddSigner({
  stage,
  world,
  profiles,
  onAdd,
}: {
  stage: Pick<EditableStage, 'members'>;
  world: TheWorld;
  profiles: TheWorld['profiles'];
  onAdd: (m: SignoffStageMember) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [ext, setExt] = useState<{ name: string; position: string; email: string } | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQ('');
    setExt(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) close();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open, close]);

  useEffect(() => {
    if (open && !ext) input.current?.focus();
  }, [open, ext]);

  const has = (m: SignoffStageMember) => stage.members.some((x) => sameMember(x, m));
  const needle = q.trim().toLowerCase();

  const roleOpts = SIGNOFF_ROLES.filter((r) => !has({ kind: 'role', role: r })).filter(
    (r) => !needle || SIGNOFF_ROLE_LABEL[r].toLowerCase().includes(needle)
  );

  const people = [...new Set(world.access.emails.map((e) => e.toLowerCase()))]
    .sort()
    .filter((e) => !has({ kind: 'person', personRef: e }))
    .map((e) => ({ email: e, name: displayName(e, profiles), position: personPosition(e, world) }))
    .filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.email.includes(needle))
    .slice(0, 8);

  const looksEmail = /@/.test(q.trim());

  const pickRole = (r: SignoffRole) => {
    onAdd({ kind: 'role', role: r });
    close();
  };
  const pickPerson = (email: string) => {
    onAdd({ kind: 'person', personRef: email });
    close();
  };
  const startExternal = () => {
    const t = q.trim();
    setExt(looksEmail ? { name: '', position: '', email: t } : { name: t, position: '', email: '' });
  };
  const saveExternal = () => {
    if (!ext) return;
    const name = ext.name.trim();
    const position = ext.position.trim();
    if (!name || !position) return;
    onAdd({ kind: 'external', name, position, email: ext.email.trim() });
    close();
  };

  return (
    <span className="sr-add-wrap" ref={wrap}>
      <button
        className="btn btn-small sr-add-signer"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => (open ? close() : setOpen(true))}
      >
        <Icon name="plus" size={15} /> Add signer
      </button>

      {open && (
        <div className="sr-ta" role="menu">
          {!ext ? (
            <>
              <input
                ref={input}
                className="input sr-ta-input"
                placeholder="Search a role, a person, or type an email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (roleOpts.length) pickRole(roleOpts[0]);
                    else if (people.length) pickPerson(people[0].email);
                    else if (needle) startExternal();
                  }
                }}
              />
              <div className="sr-ta-body">
                {roleOpts.length > 0 && (
                  <>
                    <p className="sr-ta-label">Roles — resolved fresh each time</p>
                    {roleOpts.map((r) => (
                      <button className="sr-ta-opt" key={r} onClick={() => pickRole(r)}>
                        <Icon name="users" size={16} className="sr-ta-ic" />
                        <span className="sr-ta-opt-text">
                          <span className="sr-ta-name">{SIGNOFF_ROLE_LABEL[r]}</span>
                          <span className="sr-ta-sub">
                            {r === 'owner' && isSomebody(world.owner)
                              ? `${world.owner.name} — ${world.owner.position}`
                              : SIGNOFF_ROLE_BLURB[r]}
                          </span>
                        </span>
                      </button>
                    ))}
                  </>
                )}

                {people.length > 0 && (
                  <>
                    <p className="sr-ta-label">People on this board</p>
                    {people.map((p) => (
                      <button className="sr-ta-opt" key={p.email} onClick={() => pickPerson(p.email)}>
                        <span className="sr-ta-av" aria-hidden="true">
                          {initials(p.email, profiles)}
                        </span>
                        <span className="sr-ta-opt-text">
                          <span className="sr-ta-nameline"><span className="sr-ta-name">{p.name}</span><VendorTag who={p.email} profiles={profiles} compact /></span>
                          <span className="sr-ta-sub">{p.position}</span>
                        </span>
                      </button>
                    ))}
                  </>
                )}

                {roleOpts.length === 0 && people.length === 0 && !needle && (
                  <p className="sr-ta-empty">Type a name, a role, or an email address.</p>
                )}

                {needle && (
                  <button className="sr-ta-opt sr-ta-invite" onClick={startExternal}>
                    <Icon name="mail" size={16} className="sr-ta-ic" />
                    <span className="sr-ta-opt-text">
                      <span className="sr-ta-name">
                        Add &ldquo;{q.trim()}&rdquo; {looksEmail ? 'by email' : 'as an outside signer'}
                      </span>
                      <span className="sr-ta-sub">Someone not on this board.</span>
                    </span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="sr-ext">
              <p className="sr-ta-label">Someone not on this board</p>
              <label className="sr-ext-field">
                <span className="sr-ext-lab">
                  Name <span className="req">*</span>
                </span>
                <input
                  className="input"
                  value={ext.name}
                  autoFocus
                  onChange={(e) => setExt({ ...ext, name: e.target.value })}
                />
              </label>
              <label className="sr-ext-field">
                <span className="sr-ext-lab">
                  Position <span className="req">*</span>
                </span>
                <input
                  className="input"
                  value={ext.position}
                  placeholder="e.g. Head of Information Security"
                  onChange={(e) => setExt({ ...ext, position: e.target.value })}
                />
              </label>
              <label className="sr-ext-field">
                <span className="sr-ext-lab">Email</span>
                <input
                  className="input"
                  type="email"
                  value={ext.email}
                  placeholder="So they can be sent a signing link"
                  onChange={(e) => setExt({ ...ext, email: e.target.value })}
                />
              </label>
              <div className="sr-ext-actions">
                <button className="link" onClick={() => setExt(null)}>
                  Back
                </button>
                <button
                  className="btn btn-small btn-primary"
                  disabled={!ext.name.trim() || !ext.position.trim()}
                  onClick={saveExternal}
                >
                  Add signer
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </span>
  );
}
